package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKapkanPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/Kapkan.md", FrontMatterElement.Builder { addScalar("title", "kapkan: ktlint rules with a defect behind each one"); addScalar("searchTitle", "Custom ktlint rules for Kotlin — kapkan"); addScalar("date", "2026-09-03"); addScalar("description", "A ktlint rule set with no configuration, where every rule encodes one class of defect this stack paid to find — and the two that did not survive being measured."); addScalar("library", "sborka"); }))
}

@Page("/blog/kapkan")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun KapkanPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka") {
            org.jetbrains.compose.web.dom.Text("kapkan")
        }
        org.jetbrains.compose.web.dom.Text(" is a ktlint rule set published with ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka") {
            org.jetbrains.compose.web.dom.Text("sborka")
        }
        org.jetbrains.compose.web.dom.Text(", a set of Gradle conventions. A repository that applies ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sborka.lint")
        }
        org.jetbrains.compose.web.dom.Text(" gets the formatter and the rules together, and there is no property that turns them on or off.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The rules are not opinions about style: each encodes a class of defect actually found in a working repository and carries the backlog item it was found in. A rule with nothing behind it is not added.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("detekt is configurable, which is right for a tool meant to fit any codebase. It also means ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("detekt.yml")
        }
        org.jetbrains.compose.web.dom.Text(", thresholds, severities and a baseline — four places to switch a check off where nobody reads the diff — and a default set full of taste: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LongMethod")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MagicNumber")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TooManyFunctions")
        }
        org.jetbrains.compose.web.dom.Text(". In a codebase where every number carries a comment naming its source, a rule arguing with that loses inside a week, in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Suppress")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("kapkan drops the configuration. A rule is in the set or it is not, and the one knob is a suppression that has to say why.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-catches") }) {
        org.jetbrains.compose.web.dom.Text("What it catches")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("foreign-import-in-common")
                }
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("java.*")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("javax.*")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("android.*")
            }
            org.jetbrains.compose.web.dom.Text(" or ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("org.w3c.*")
            }
            org.jetbrains.compose.web.dom.Text(" in a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("common…")
            }
            org.jetbrains.compose.web.dom.Text(" source set. The compiler already answers this for any module with a non-JVM target; the rule answers it forty seconds earlier, and covers the module whose only targets are the JVM and Android.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("swallowed-failure")
                }
            }
            org.jetbrains.compose.web.dom.Text(" — a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("runCatching")
            }
            org.jetbrains.compose.web.dom.Text(" whose failure nothing reads and whose value the language throws away, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("catch (e: Exception)")
            }
            org.jetbrains.compose.web.dom.Text(" whose body never mentions ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("e")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("wall-clock")
                }
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("System.currentTimeMillis()")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Clock.System.now()")
            }
            org.jetbrains.compose.web.dom.Text(" and the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("java.time")
            }
            org.jetbrains.compose.web.dom.Text(" neighbours, anywhere but the place a repository says owns the clock.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("suppression-needs-a-reason")
                }
            }
            org.jetbrains.compose.web.dom.Text(" — a kapkan rule switched off without a sentence beside it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("kapkanJoins")
                }
            }
            org.jetbrains.compose.web.dom.Text(" — a Gradle task rather than a rule: declarations nothing outside their own file mentions, and functions nothing calls.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("installation") }) {
        org.jetbrains.compose.web.dom.Text("Installation")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Published to a snapshot repository, not to Maven Central.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// settings.gradle.kts
pluginManagement {
    repositories {
        gradlePluginPortal()
        mavenCentral()
        maven("https://reposilite.kotlin.website/snapshots") {
            content { includeGroupByRegex("ru\\.workinprogress.*") }
        }
    }
}

plugins { id("ru.workinprogress.sborka.settings") version "0.2.0.28" }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A module then applies ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("id(\"ru.workinprogress.sborka.lint\")")
        }
        org.jetbrains.compose.web.dom.Text(" beside its Kotlin plugin.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The version is pinned twice, and the two are different halves.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("settings.gradle.kts")
        }
        org.jetbrains.compose.web.dom.Text(" names the settings plugin; the project conventions are named wherever the root build puts them on the plugin classpath, usually a version catalog. Bumping one and not the other is silent — a new settings plugin, the old conventions, a green build and no rule running.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-a-finding-is-answered") }) {
        org.jetbrains.compose.web.dom.Text("How a finding is answered")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""RiderModule.kt:230:52: Clock.System.now() reads the clock of whatever machine this runs on — a time
that has to agree with somebody else's is a value carried on the wire, not one read here
(kapkan:wall-clock)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Some findings are correct code. A composition root binding a clock is the shape the rule pushes towards, and it is still a wall-clock read:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@Suppress(
    "ktlint:kapkan:wall-clock",
    "the composition root is where the clock is bound; the view model counts a duration it was handed",
)
public fun riderModule(config: RiderConfig): Module = module { /* … */ }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Suppress")
        }
        org.jetbrains.compose.web.dom.Text(" rather than an annotation of kapkan's own, and not for taste: one used in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonMain")
        }
        org.jetbrains.compose.web.dom.Text(" would need a multiplatform artefact on the compile classpath of every module of every repository — jvm, wasmJs, android, three iOS — with a release cycle of its own. It also makes the suppression a release check, because ktlint validates the id against the rules it loaded and refuses one it does not know: a build where the jar never reached the worker fails on the suppression instead of quietly running fewer rules.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-rules-that-were-measured-and-not-written") }) {
        org.jetbrains.compose.web.dom.Text("Two rules that were measured and not written")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("\"A ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("@Test")
            }
            org.jetbrains.compose.web.dom.Text(" that returns a value never runs\"")
        }
        org.jetbrains.compose.web.dom.Text(" is a real defect — JUnit does not collect a non-void method and does not warn — but not a syntactic one. A regular expression for \"")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Test")
        }
        org.jetbrains.compose.web.dom.Text(" with an expression body\" fires ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("471 times across four repositories")
        }
        org.jetbrains.compose.web.dom.Text(" on healthy code, because ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("runTest")
        }
        org.jetbrains.compose.web.dom.Text(" returns ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TestResult")
        }
        org.jetbrains.compose.web.dom.Text(", which is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("typealias TestResult = Unit")
        }
        org.jetbrains.compose.web.dom.Text(" on the JVM. sborka answers it after the run instead, comparing the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Test")
        }
        org.jetbrains.compose.web.dom.Text(" methods the sources declare against what JUnit reported.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("And ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("swallowed-failure")
            }
            org.jetbrains.compose.web.dom.Text(" shipped asking the wrong question")
        }
        org.jetbrains.compose.web.dom.Text(" — whether the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Result")
        }
        org.jetbrains.compose.web.dom.Text(" is discarded, rather than whether the failure is read. On the first repository it ran against, five of nine findings were ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("runCatching { … }.onFailure { log.warn(…) }")
        }
        org.jetbrains.compose.web.dom.Text(" as a statement, which is correct code, against four real ones. A chain mentioning ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("onFailure")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getOrElse")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fold")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("recover")
        }
        org.jetbrains.compose.web.dom.Text(" now ends the question; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getOrDefault")
        }
        org.jetbrains.compose.web.dom.Text(" deliberately do not, because they answer \"what is the value\" and drop the exception on the way.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("kapkanjoins") }) {
        org.jetbrains.compose.web.dom.Text("kapkanJoins")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The one rule no regular expression can find. It reads the constant pool of every module's class files, resolves calls up the class hierarchy, strips the suffix Kotlin mangles onto anything mentioning a value class, and lists two piles: what nothing outside its own file mentions, and what nothing calls at all.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The second pile is the interesting one — four of the most expensive defects in the reference service had that shape, including a payment gateway's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("capture")
        }
        org.jetbrains.compose.web.dom.Text(" implemented and called by nobody.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On a 33 000-line repository it reads 1563 class files, considers 333 types and 253 functions, and reports ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("29 findings of which 24 are \"only tests reach it\"")
        }
        org.jetbrains.compose.web.dom.Text(". None of the 29 is a defect, which is why it is a report and not a gate: an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("inline")
        }
        org.jetbrains.compose.web.dom.Text(" function leaves no reference behind, a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("const val")
        }
        org.jetbrains.compose.web.dom.Text(" is folded into its call site, and declaring a helper beside its only user is Kotlin's own idiom.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-cost-to-switch-on") }) {
        org.jetbrains.compose.web.dom.Text("What it cost to switch on")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same build box, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./gradlew check --rerun-tasks")
        }
        org.jetbrains.compose.web.dom.Text(", three runs per variant, browser suite skipped on both sides: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("54.03 / 47.24 / 43.04 s before, 51.43 / 41.55 / 41.54 s after")
        }
        org.jetbrains.compose.web.dom.Text(". The spread inside each triple is larger than the difference between them, so the honest statement is that the rules cost less than the noise of the stand — not \"zero because the ktlint task was already running\", which would be true of the parse and false of the AST walk.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The other price is fourteen suppressions in that repository, each with a reason: four composition roots binding a clock, three defaults of an injectable one, three saga tests that need a clock and assert on something else, two ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("finally { runCatching { close() } }")
        }
        org.jetbrains.compose.web.dom.Text(", a crash handler of last resort, and a degradation sink that must not be able to break the screen it reports a hole in.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Pinned against ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("ktlint 1.8.0, ktlint-gradle 14.2.0 and Kotlin 2.4.10")
            }
            org.jetbrains.compose.web.dom.Text(", and run on nothing else. The ktlint rule API changed shape between 0.x and 1.x and can again.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("kapkanJoins")
            }
            org.jetbrains.compose.web.dom.Text(" reads ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("JVM class files only")
            }
            org.jetbrains.compose.web.dom.Text(". Kotlin/Native and wasm keep their references inside a serialised IR with no supported reader. Every application here keeps a JVM or desktop target anyway, for screenshot tests, but that is a property of this portfolio and not of the stack.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A Koin registration is a call.")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("single { SendReceiptUseCase(get()) }")
            }
            org.jetbrains.compose.web.dom.Text(" compiles to a class whose constant pool names the constructor, so \"registered and never ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("get()")
            }
            org.jetbrains.compose.web.dom.Text("-ed\" is invisible. Generated code counts as a caller for the same reason.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("wall-clock")
            }
            org.jetbrains.compose.web.dom.Text(" matches receivers by name, so an aliased import defeats it. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("System.nanoTime()")
            }
            org.jetbrains.compose.web.dom.Text(" is deliberately absent — a monotonic clock cannot be subtracted from anybody's epoch.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Published with CI build numbers to a snapshot repository. There is no release line yet.")
        }
    }
}
